<template>
  <section v-if="shouldRender" class="menu-info-bar">
    <div class="menu-info-bar__container container">
      <div class="menu-info-bar__texts">
        <p v-if="activeMenuDetails?.description" class="menu-info-bar__description">
          {{ activeMenuDetails.description }}
        </p>
        <div
          v-if="!activeMenuDetails?.isTimesInfoHidden && servingHours"
          class="menu-info-bar__serving-hours"
        >
          <i class="icon icon-Cutlry" aria-hidden="true" />
          <span>
            <strong>{{ t('iorder-served-today') }}:</strong>
            {{ servingHours }}
          </span>
        </div>
      </div>
      <div class="menu-info-bar__links">
        <template v-if="links.length">
          <div v-for="link in links" :key="link.value.key" class="menu-info-bar__link">
            <base-link
              :data-test-id="link.value.key"
              class="link"
              :field="link"
              @click="registerDLEvent(link.value)"
            />
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { MenuInfoBarProps } from './types';
import type { MenuDetails } from '../types';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseLink from '../../2_elements/BaseLink/BaseLink.vue';
import { useMenuInteractionTracking } from '../../../data-layer/hooks/useMenuInteractionTracking';

const { t } = useI18n();

const { trackMenuInteraction } = useMenuInteractionTracking();

const props = defineProps<MenuInfoBarProps>();

const activeMenuDetails = computed(() => {
  return {
    ...props.menusDetails?.value?.find(
      (details: MenuDetails) => details.key === props.activeMenuItem?.name
    ),
  };
});

const servingHours = computed(() => {
  if (!props.currentDayOpeningHours?.value) return t('pub-closed');

  return props.activeMenuItem?.tags?.includes('FOOD')
    ? props.currentDayServingHours?.value
    : props.currentDayOpeningHours?.value;
});

const links = computed(() => {
  let _links = [];

  const downloadMenuLink = props.downloadMenuCta?.value?.find(
    (cta) => cta.iOrderMenuKey === props.activeMenuItem?.name
  );
  if (downloadMenuLink?.href) {
    _links.push({
      value: {
        href: downloadMenuLink.href,
        text: t('iorder-download-menu'),
        key: 'download-menu-link',
        target: '_blank',
        dl_menuSection: props.activeMenuItem.name,
      },
    });
  }
  if (activeMenuDetails.value?.nutritionalInfoCta) {
    _links.push({
      value: {
        href: activeMenuDetails.value.nutritionalInfoCta,
        text: t('iorder-nutritional-info'),
        key: 'nutritional-info-link',
        target: '_blank',
        dl_menuSection: 'nutritional',
      },
    });
  }
  if (props.allergenData?.allergensLink?.href) {
    _links.push({
      value: {
        href: props.allergenData.allergensLink.href,
        text: t('iorder-allergen-info'),
        key: 'allergen-info-link',
        target: '_blank',
        dl_menuSection: 'allergens',
      },
    });
  }
  return _links;
});

const shouldRender = computed(
  () =>
    activeMenuDetails.value?.description ||
    (!activeMenuDetails.value?.isTimesInfoHidden && servingHours.value) ||
    props.allergenData?.allergensLink?.href ||
    links.value?.length
);

const registerDLEvent = ({ href, dl_menuSection }: { href: string; dl_menuSection: string }) => {
  trackMenuInteraction(href, dl_menuSection, 'download');
};
</script>

<style lang="scss">
@import './scss/MenuInfoBar.scss';
</style>
